import { useEffect, useRef, useState } from "react";
import type { StreamStep, TemplateGenerationPhase } from "./templateGenerationStream";
import { getStageIcon } from "./templateGenerationStream";
import "./TemplateGenerationProgress.css";

const RENDER_STAGES = new Set(["generating_image", "generating_video", "itinerary_render", "rendering_html", "generating_itinerary"]);

function formatRemaining(sec: number): string {
    if (sec <= 0) return "finishing up…";
    if (sec >= 60) return `~${Math.floor(sec / 60)}m ${sec % 60}s left`;
    return `~${sec}s left`;
}

function ElapsedTimer({ startedAt }: { startedAt: number }) {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
        return () => clearInterval(t);
    }, [startedAt]);
    if (elapsed < 2) return null;
    return <span className="tgp-step__elapsed">{elapsed}s</span>;
}

function ThinkingDots() {
    return <span className="tgp-thinking-dots"><span /><span /><span /></span>;
}

export type TemplateGenerationProgressProps = {
    steps: StreamStep[];
    progress: number;
    phase: TemplateGenerationPhase;
    mediaType?: "image" | "video" | string;
    assetType?: string;
    templateName?: string;
    showSkeleton?: boolean;
    fallbackTitle?: string;
};

export default function TemplateGenerationProgress({
    steps,
    progress,
    phase,
    mediaType = "image",
    assetType,
    templateName,
    showSkeleton = false,
    fallbackTitle = "Generating…",
}: TemplateGenerationProgressProps) {
    const isVideo = mediaType === "video" || assetType === "video";
    const isItinerary = assetType === "itinerary";
    const pct = Math.min(100, Math.max(0, progress));
    const listRef = useRef<HTMLOListElement>(null);

    void phase;

    useEffect(() => {
        if (listRef.current) {
            const last = listRef.current.lastElementChild;
            last?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [steps.length]);

    const lastStep = steps[steps.length - 1];
    const isRendering = lastStep && RENDER_STAGES.has(lastStep.stage);

    const renderStartRef = useRef<number | null>(null);
    if (isRendering && renderStartRef.current === null) {
        renderStartRef.current = Date.now() - ((lastStep.elapsed_seconds ?? 0) * 1000);
    }
    if (!isRendering) renderStartRef.current = null;

    const badgeLabel = isItinerary ? "Itinerary" : isVideo ? "Video" : "Flyer";
    const badgeClass = isItinerary ? "tgp__badge--itinerary" : isVideo ? "tgp__badge--video" : "tgp__badge--flyer";

    return (
        <div className="tgp" role="status" aria-live="polite" aria-busy="true">
            {/* ── Header ── */}
            <div className="tgp__eyebrow">
                {templateName ? <span className="tgp__template">{templateName}</span> : null}
                <span className={`tgp__badge ${badgeClass}`}>
                    <span className="tgp__badge-dot" aria-hidden="true" />
                    {badgeLabel} generation
                </span>
            </div>

            {/* ── Steps ── */}
            {steps.length === 0 ? (
                <div className="tgp__placeholder-wrap">
                    <span className="tgp__placeholder-spinner" />
                    <p className="tgp__placeholder">{fallbackTitle}</p>
                </div>
            ) : (
                <ol className="tgp__steps" ref={listRef}>
                    {steps.map((step, i) => {
                        const isActive = i === steps.length - 1;
                        const isRenderStep = RENDER_STAGES.has(step.stage);
                        const icon = getStageIcon(step.stage);

                        return (
                            <li
                                key={`${step.stage}-${i}`}
                                className={`tgp-step${isActive ? " tgp-step--active" : " tgp-step--done"}`}
                            >
                                {/* Status dot */}
                                <span className="tgp-step__icon" aria-hidden="true">
                                    {isActive
                                        ? <span className="tgp-step__spinner" />
                                        : <span className="tgp-step__check">✓</span>
                                    }
                                </span>

                                <span className="tgp-step__body">
                                    {/* Emoji + title row */}
                                    <span className="tgp-step__title-row">
                                        <span className="tgp-step__emoji" aria-hidden="true">{icon}</span>
                                        <span className="tgp-step__title">
                                            {step.title}
                                            {isActive && !isRenderStep && <ThinkingDots />}
                                        </span>
                                        {!isActive && step.elapsed_seconds != null && (
                                            <span className="tgp-step__elapsed tgp-step__elapsed--done">{step.elapsed_seconds}s</span>
                                        )}
                                    </span>

                                    {/* Detail line — visible on active step */}
                                    {isActive && step.message && step.message !== step.title && (
                                        <span className="tgp-step__detail">{step.message}</span>
                                    )}

                                    {/* Render countdown */}
                                    {isActive && isRenderStep && (
                                        <span className="tgp-step__render-info">
                                            {step.remaining_seconds != null && step.remaining_seconds > 0 && (
                                                <span className="tgp-step__remaining">
                                                    {formatRemaining(step.remaining_seconds)}
                                                </span>
                                            )}
                                            {renderStartRef.current != null && (
                                                <ElapsedTimer startedAt={renderStartRef.current} />
                                            )}
                                        </span>
                                    )}
                                </span>
                            </li>
                        );
                    })}
                </ol>
            )}

            {/* ── Progress bar ── */}
            <div className="tgp__bar-wrap" aria-hidden="true">
                <div className="tgp__bar-track">
                    <div
                        className="tgp__bar-fill"
                        style={{ width: `${pct}%` }}
                    />
                    {pct > 0 && pct < 100 && (
                        <div className="tgp__bar-glow" style={{ left: `calc(${pct}% - 12px)` }} />
                    )}
                </div>
                <span className="tgp__bar-pct">{pct}%</span>
            </div>

            {/* ── Preview skeleton ── */}
            {showSkeleton ? (
                <div
                    className={`tgp-skeleton${isVideo ? " tgp-skeleton--video" : isItinerary ? " tgp-skeleton--itinerary" : " tgp-skeleton--flyer"}`}
                    aria-label={isVideo ? "Video preview loading" : isItinerary ? "Itinerary preview loading" : "Flyer preview loading"}
                >
                    <div className="tgp-skeleton__shimmer" />

                    {isItinerary ? (
                        <div className="tgp-skeleton__itin-body">
                            {/* Cover bar */}
                            <div className="tgp-skeleton__itin-cover" />
                            {/* Day lines */}
                            <div className="tgp-skeleton__itin-lines">
                                {[75, 55, 88, 45, 68, 52, 80, 40].map((w, idx) => (
                                    <div
                                        key={idx}
                                        className={`tgp-skeleton__itin-line${idx % 3 === 0 ? " tgp-skeleton__itin-line--heading" : ""}`}
                                        style={{ width: `${w}%` }}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : isVideo ? (
                        <div className="tgp-skeleton__video-body">
                            <div className="tgp-skeleton__play" aria-hidden="true"><span>▶</span></div>
                            <div className="tgp-skeleton__content">
                                <div className="tgp-skeleton__line tgp-skeleton__line--title" />
                                <div className="tgp-skeleton__line tgp-skeleton__line--sub" />
                            </div>
                        </div>
                    ) : (
                        <div className="tgp-skeleton__content">
                            <div className="tgp-skeleton__line tgp-skeleton__line--brand" />
                            <div className="tgp-skeleton__line tgp-skeleton__line--title" />
                            <div className="tgp-skeleton__line tgp-skeleton__line--sub" />
                            <div className="tgp-skeleton__cta" />
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
